import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { readCurrentUser } from './sessionCheck';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { LogIn, Menu as MenuIcon, Search } from 'lucide-react';
import GlobalSearch from '../../features/search/GlobalSearch';
import NotificationBell from '../../features/notifications/components/NotificationBell';
import { RealtimeProvider, useRealtime } from '../../shared/realtime/RealtimeProvider';
import { Link, useLocation } from 'react-router';
import { API_URL } from '../../config';
import { useI18n } from '../../i18n/I18nProvider';
import { clearLegacyOrgState, createAuthHeaders, getAuthToken } from '../../shared/api/auth';
import { useOrganization } from '../../features/organization/OrganizationContext';
import createBerryTheme, { applyBrandCssVars, applySidebarCssVars } from '../theme/createBerryTheme';
import SidebarMenu from './SidebarMenu';
import UserMenu from './UserMenu';
import { getRouteLabels, getRouteSubtitles } from './navigation';
import AppRoutes from '../routes/AppRoutes';
import CreateOrgModal from '../../features/organization/CreateOrgModal';
import QuietModeBubble, { useQuietModeStatus } from '../../features/admin/platform/QuietModeBubble';
import { canManageContent } from '../../shared/hooks/useModuleAccess';
import { BetaBadge } from '../../shared/ui/BetaBadge';
import { HelpProvider } from '../../help/HelpContext';
import HelpButton from '../../help/HelpButton';
import { isImpersonating as readImpersonating } from '../../shared/api/impersonation';
import ImpersonationBanner from './ImpersonationBanner';
import { drawerSx } from './drawerStyles';
import { recordRecentPage } from './recentPages';
import { OPEN_GLOBAL_SEARCH } from './globalSearchEvent';
import AppFooter from '../../features/legal/AppFooter';
import { usePageTitleOverride } from './pageTitleOverride';
import LegalNotice from '../../features/legal/LegalNotice';
import '../App.css';


/** Keeps the signed-in user's points and level current when the server says they changed. */
function LivePointsSync({ onBalance }: { onBalance: (b: { earned: number; spendable: number; level: string }) => void }) {
  useRealtime('/user/queue/points', (push) => {
    if (push?.kind === 'balance') onBalance(push);
  });
  return null;
}

const DRAWER_WIDTH_FULL = 248;
const DRAWER_WIDTH_RAIL = 48;
const STATUS_BANNER_HEIGHT = 34;
const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

export default function AppShell() {
  const { t } = useI18n();
  const { setOrganizations, setSignedInUserId } = useOrganization();
  // One shared Kudos, one look (ADR-025; owner, 2026-10-03): the Kudos red and
  // the Kudos sidebar for everyone. The per-organisation colours stored on old
  // rows are no longer read.
  const muiTheme = useMemo(() => createBerryTheme(null), []);

  useEffect(() => {
    applyBrandCssVars(null);
    applySidebarCssVars(null);
  }, []);
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const location = useLocation();

  const isImpersonating = readImpersonating();
  // One space (ADR-025): whatever the old organisation switcher stored is stale.
  useEffect(() => { clearLegacyOrgState(); }, []);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(getAuthToken()));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // The top bar has no rule until the page scrolls, so it reads as one sheet (spec 4.2).
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const applyUser = useCallback((data: any) => {
    // Transform snake_case from backend to camelCase for frontend
    const normalizedUser = {
      ...data,
      moduleAccess: data.module_access || data.moduleAccess || [],
      activeOrganizationId: data.active_organization_id || data.activeOrganizationId,
      isAdmin: data.is_admin !== undefined ? data.is_admin : data.isAdmin,
      isHr: data.is_hr !== undefined ? data.is_hr : data.isHr,
      totalPoints: data.total_points !== undefined ? data.total_points : data.totalPoints,
      createdAt: data.created_at || data.createdAt,
      platformRole: data.platform_role || data.platformRole,
      orgRoles: data.org_roles || data.orgRoles,
      organizations: Array.isArray(data?.organizations) ? data.organizations.map((org: any) => ({
        organizationId: org.organization_id || org.organizationId,
        organizationName: org.organization_name || org.organizationName,
        role: org.role,
        status: org.status,
      })) : [],
    };
    setUser(normalizedUser);
    if (Array.isArray(normalizedUser?.organizations)) {
      setOrganizations(normalizedUser.organizations);
    }
  }, [setOrganizations]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/users/me`, {
      headers: createAuthHeaders(),
    })
      .then((res) => readCurrentUser(res, () => localStorage.removeItem('token')))
      .then((data) => {
        if (data?.id) {
          applyUser(data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [applyUser]);

  // The organisation's modules and switches are read once someone is signed in, and again
  // right after signing in, rather than refused before it (and missing until a reload).
  const signedInId = (user as any)?.id ?? null;
  useEffect(() => { setSignedInUserId(signedInId); }, [signedInId, setSignedInUserId]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // The Admin Center has its own search for pages and settings on the same keys.
        if (window.location.pathname.startsWith('/admin')) return;
        e.preventDefault();
        if (user) setGlobalSearchOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [user]);

  useEffect(() => {
    const open = () => { if (user) setGlobalSearchOpen(true); };
    window.addEventListener(OPEN_GLOBAL_SEARCH, open);
    return () => window.removeEventListener(OPEN_GLOBAL_SEARCH, open);
  }, [user]);

  const isAuthRoute = location.pathname === '/login' || location.pathname === '/signup';
  // Signed out, a legal page draws its own footer (the shell has no sidebar or header then).
  const isPublicLegal = !user && location.pathname.startsWith('/legal/');
  const isAdminRoute = location.pathname.startsWith('/admin');
  // The event page and the event editor (/event/:id, /event/:id/edit, /events/new) draw their own top bar.
  const isEventDetailRoute = location.pathname.startsWith('/event/') || location.pathname === '/events/new';
  const isLearningDetailRoute = /^\/learning\/[^/]+/.test(location.pathname);
  const isLearningHubRoute = location.pathname === '/learning';
  // Rail width only applies on desktop, where Admin Center's own panel sits next to
  // it. On mobile the drawer is opened via the hamburger as the only way out of Admin
  // Center, and needs full width for the full labeled sidebar (see collapsed below) —
  // a 48px-wide drawer squeezed with full sidebar content inside is unusable.
  const drawerWidth = isAdminRoute && isDesktop ? DRAWER_WIDTH_RAIL : DRAWER_WIDTH_FULL;
  const routeLabels = getRouteLabels(t);
  const routeSubtitles = getRouteSubtitles(t);

  // Quiet mode: people who manage things (super admins, admins, HR, editors) see a small
  // bubble saying nobody is being told anything. Members see nothing.
  const managesThings = !!user && ((user as any).platformRole === 'super_admin' || canManageContent(user));
  const quiet = useQuietModeStatus(managesThings);
  const showQuietBubble = quiet.on && managesThings && !isImpersonating && !isAuthRoute;
  const showStatusBanner = isImpersonating;
  // Pages with their own fixed layout (the Admin Center) read this to sit below the
  // impersonation strip.
  useEffect(() => {
    document.documentElement.style.setProperty('--app-status-banner', showStatusBanner ? `${STATUS_BANNER_HEIGHT}px` : '0px');
  }, [showStatusBanner]);

  let pageTitle = t('app.name');
  if (location.pathname.startsWith('/event/')) pageTitle = t('routes.eventDetails');
  else if (location.pathname.startsWith('/learning/')) pageTitle = t('routes.course');
  // A person's profile. Without this the header read "Kudos" -- the fallback --
  // while the page below it was plainly about someone.
  else if (/^\/people\/[^/]+$/.test(location.pathname)) pageTitle = t('routes.personProfile');
  // One certificate, and checking one: they read "Kudos", the fallback, above the certificate.
  else if (/^\/certificates\/[^/]+$/.test(location.pathname)) pageTitle = t('routes.certificate');
  else if (/^\/verify\/[^/]+$/.test(location.pathname)) pageTitle = t('routes.verifyCertificate');
  else if (location.pathname === '/legal/privacy') pageTitle = t('legal.privacy');
  else if (location.pathname === '/legal/terms') pageTitle = t('legal.terms');
  else if (location.pathname === '/use-cases/new') pageTitle = t('routes.useCaseNew');
  else if (/^\/use-cases\/[^/]+(\/edit)?$/.test(location.pathname)) pageTitle = t('routes.useCase');
  else if (location.pathname === '/proposals/new') pageTitle = t('routes.proposalNew');
  else if (/^\/proposals\/[^/]+(\/edit)?$/.test(location.pathname)) pageTitle = t('routes.proposal');
  else pageTitle = routeLabels[location.pathname] || t('app.name');
  // The 404 and 403 name themselves (they can be on any path).
  const titleOverride = usePageTitleOverride();
  const shownTitle = titleOverride ?? pageTitle;

  const pageSubtitle = routeSubtitles[location.pathname];

  // Remember pages with a real name for the 404 and the search's "Recent" list. A path
  // with no name of its own (the 404 itself) falls back to the app name and is skipped.
  useEffect(() => {
    if (!user || isAuthRoute || pageTitle === t('app.name') || location.pathname === '/403') return;
    // The path alone: a page's tabs and filters (?tab=…) are the same page, not a second entry.
    recordRecentPage({ path: location.pathname, title: pageTitle });
  }, [user, isAuthRoute, pageTitle, location.pathname, t]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <RealtimeProvider userId={user?.id}>
      {/* Help: one button in every header, a side panel, the tours (src/help). No page, no menu entry. */}
      <HelpProvider user={user} readOnly={isImpersonating}>
      <LivePointsSync onBalance={(b) => setUser((u: any) => (u ? { ...u, totalPoints: b.earned, total_points: b.earned, spendable_points: b.spendable, level: b.level } : u))} />
      <CssBaseline />
      {/* Visible only on focus -- allows keyboard users to skip the sidebar */}
      <a href="#main-content" className="skip-link">{t('ui.skipToMain')}</a>
      {isImpersonating && <ImpersonationBanner fallbackName={(user as any)?.name || (user as any)?.email} />}
      {showQuietBubble && <QuietModeBubble canManage={quiet.canManage} />}
      {/* The privacy notice on first sign-in and when it changes; then, once, the review prompt. */}
      {user && !isAuthRoute && <LegalNotice key={(user as any).id} readOnly={isImpersonating} />}
      <Box className="app-shell app-shell--root" style={showStatusBanner ? { paddingTop: STATUS_BANNER_HEIGHT } : undefined}>
        {!isAuthRoute && user && (
          <>
            <Drawer
              variant={isDesktop ? 'permanent' : 'temporary'}
              open={isDesktop ? true : mobileOpen}
              onClose={() => setMobileOpen(false)}
              sx={drawerSx({ docked: isDesktop, width: drawerWidth, bannerHeight: showStatusBanner ? STATUS_BANNER_HEIGHT : 0 })}
            >
              <SidebarMenu
                user={user}
                onNavigate={() => setMobileOpen(false)}
                onCreateOrg={() => setShowCreateOrg(true)}
                // Collapsed to an icon rail only on desktop, where it sits next to Admin
                // Center's own detailed section panel. On mobile the rail is the ONLY
                // way out of Admin Center when opened via the hamburger, so it needs the
                // full labeled sidebar like every other route gets — otherwise it reads
                // as more Admin Center-internal navigation, not an exit.
                collapsed={isAdminRoute && isDesktop}
              />
            </Drawer>

            {!isAdminRoute && !isEventDetailRoute && !isLearningDetailRoute && !isLearningHubRoute && <AppBar
              position="fixed"
              color="inherit"
              elevation={0}
              className={`app-shell__app-bar${scrolled ? ' app-shell__app-bar--scrolled' : ''}`}
              sx={{
                left: isDesktop ? `${drawerWidth}px` : 0,
                width: isDesktop ? `calc(100% - ${drawerWidth}px)` : '100%',
                top: showStatusBanner ? STATUS_BANNER_HEIGHT : 0,
                transition: 'left var(--dur-slow) var(--ease-standard), width var(--dur-slow) var(--ease-standard), top var(--dur-slow) var(--ease-standard), border-color var(--dur-fast) var(--ease-standard)',
              }}
            >
              <Toolbar className="app-shell__toolbar">
                <Box className="app-shell__header-left">
                  {!isDesktop && (
                    <IconButton edge="start" onClick={() => setMobileOpen(true)} className="app-shell__menu-toggle" aria-label={t('learningHub.openNavMenu')} data-tour="app-menu-toggle">
                      <MenuIcon size={20} aria-hidden="true" />
                    </IconButton>
                  )}

                  {!isDesktop && (
                    <>
                      <Box
                        component="img"
                        src="/favicon.svg"
                        alt={t('app.name')}
                        className="app-shell__compact-brand"
                      />
                      <BetaBadge className="beta-badge--compact" />
                    </>
                  )}

                  <Box className="app-shell__page-meta">
                    <Typography variant="h6" component="p" className="app-shell__page-title">{shownTitle}</Typography>
                    {pageSubtitle && (
                      <Typography variant="caption" component="p" className="app-shell__page-subtitle">
                        {pageSubtitle}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* The events search moved onto the Events page, next to its filters. */}
                <Box className="app-shell__header-right">
                  {user && (
                    <>
                      {/* A 240px pill with the key hint; below 900px just the icon (App.css). */}
                      <button
                        type="button"
                        onClick={() => setGlobalSearchOpen(true)}
                        className="app-shell__search"
                        aria-label={t('shell.searchLabel')}
                        aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
                        title={t('ui.searchShortcut')}
                      >
                        <Search size={16} className="app-shell__search-icon" aria-hidden="true" />
                        <span className="app-shell__search-label" aria-hidden="true">{t('shell.searchPill')}</span>
                        <kbd className="app-shell__search-kbd" aria-hidden="true">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
                      </button>
                      <HelpButton className="app-shell__header-help" />
                      <NotificationBell />
                    </>
                  )}

                  {user ? (
                    <UserMenu user={user} />
                  ) : (
                    <Button component={Link} to="/login" startIcon={<LogIn size={16} />} variant="outlined">{t('navigation.logIn')}</Button>
                  )}
                </Box>
              </Toolbar>
            </AppBar>}
          </>
        )}

        <Box
          id="main-content"
          className="app-shell__main"
          component="main"
          sx={{
            mt: !isAuthRoute && !isAdminRoute && !isEventDetailRoute && !isLearningDetailRoute && !isLearningHubRoute ? '56px' : 0,
            ...((isEventDetailRoute || isLearningDetailRoute || isLearningHubRoute) && { p: '0 !important' }),
          }}
        >
          <AppRoutes
            loading={loading}
            user={user}
            setUser={applyUser}
            onOpenSidebar={() => setMobileOpen(true)}
          />
          {/* One footer for every page (privacy plan item 8). The sign-in page draws it inside
              its own layout and the Admin Center has none; full-bleed pages get the page gutter. */}
          {!loading && !isAuthRoute && !isAdminRoute && !isPublicLegal && (
            <div className={isEventDetailRoute || isLearningDetailRoute || isLearningHubRoute ? 'app-shell__footer app-shell__footer--bleed' : 'app-shell__footer'}>
              <AppFooter />
            </div>
          )}
        </Box>
      </Box>
      <CreateOrgModal open={showCreateOrg} onClose={() => setShowCreateOrg(false)} />
      <GlobalSearch open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} user={user} />
      </HelpProvider>
      </RealtimeProvider>
    </MuiThemeProvider>
  );
}
